import React from 'react';
import {
  AutocompleteClasses,
  AutocompleteRenderGroupParams,
  AutocompleteRenderOptionState,
} from '@mui/material/Autocomplete';
import { AutocompleteGroupedOption } from '@mui/material/useAutocomplete';

import Box from '../../Box';
import { eventPreventDefault } from '../../utils/events';

interface RenderNoOptionsParams {
  classes?: Partial<AutocompleteClasses>;
  noOptionsText: React.ReactNode;
}

// Prevent input blur when interacting with the "no options" content
export const renderNoOptions = ({ classes, noOptionsText }: RenderNoOptionsParams) => (
  <Box className={classes?.noOptions} role="presentation" onMouseDown={eventPreventDefault}>
    {noOptionsText}
  </Box>
);

interface RenderGroupedOptionsParams<T> {
  classes?: Partial<AutocompleteClasses>;
  getOptionProps: (params: { index: number; option: T }) => React.HTMLAttributes<HTMLLIElement>;
  groupBy?: ((option: T) => string) | undefined;
  groupedOptions: AutocompleteGroupedOption<T>[] | T[];
  inputValue: string;
  renderGroup?: (params: AutocompleteRenderGroupParams) => React.ReactNode;
  renderOption: (option: T, state: AutocompleteRenderOptionState) => React.ReactNode;
}

export const renderGroupedOptions = <T,>(params: RenderGroupedOptionsParams<T>) => {
  const {
    classes,
    getOptionProps,
    groupBy,
    groupedOptions,
    inputValue,
    renderGroup = (group) => (
      <li key={group.key}>
        <Box className={classes?.groupLabel}>{group.group}</Box>
        <ul className={classes?.groupUl}>{group.children}</ul>
      </li>
    ),
    renderOption,
  } = params;

  const renderListOption = (option: T, index: number) => {
    const optionProps = getOptionProps({ index, option });

    return (
      <li className={classes?.option} {...optionProps} key={optionProps.id}>
        {renderOption(option, {
          index,
          inputValue,
          selected: Boolean(optionProps['aria-selected']),
        })}
      </li>
    );
  };

  // @ts-ignore
  return groupedOptions.map((option, index) => {
    if (groupBy) {
      const { group, index: groupIndex, key, options } = option as AutocompleteGroupedOption<T>;

      return renderGroup({
        children: options.map((groupOption, groupOptionIndex) =>
          renderListOption(groupOption as T, groupIndex + groupOptionIndex),
        ),
        group,
        key: key.toString(),
      });
    }

    return renderListOption(option as T, index);
  });
};
